import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  checkAuthEmailVerified,
  sendOtpAuthEmailVerification,
  verifyOtpAuthEmailVerification, // Add this API endpoint
} from "../../assets/api/BaseURL";
import axios from "axios";
import { useToast } from "../../../context/ToastContext";

const EMAIL_BANNER_MOUNT_ID = "vyfoo-email-banner-mount";

/**
 * @param {"inline"|"globalPortal"} mode - inline: inside page content (legacy). globalPortal: banner portals into `.main-panel .content-wrapper` (body column), modals stay on `document.body`.
 * @param {number} navOffsetPx - top offset in px for inline sticky; 97 for fixed navbar layout.
 * @param {string} contentKey - bump when the route/layout changes so the banner re-attaches to the new `.content-wrapper`.
 */
export default function SlidingText({
  mode = "inline",
  navOffsetPx = 97,
  /** When this changes (e.g. route), re-attach banner mount to the current page body column */
  contentKey = "",
}) {
  const { addToast } = useToast();

  // Store auth token
  const [token, setToken] = useState("");

  // Email verification state (default true = safe fallback)
  const [isVerified, setIsVerified] = useState(true);

  // Modal visibility state
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  
  // OTP verification modal visibility
  const [showOtpModal, setShowOtpModal] = useState(false);

  // Loading state for buttons
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  
  // OTP input state
  const [otp, setOtp] = useState("");
  
  // Timer state for resend OTP
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  /** DOM node inside `.main-panel .content-wrapper` where the global banner is portaled */
  const [bannerMountEl, setBannerMountEl] = useState(null);

  // Load token from localStorage (runs once)
  useEffect(() => {
    const storedToken = localStorage.getItem("token");

    if (storedToken) {
      console.log("Token loaded from localStorage");
      setToken(storedToken);
    } else {
      console.warn("No token found in localStorage");
    }
  }, []);

  // Timer effect for countdown
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            setCanResend(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // Check email verification status
  useEffect(() => {
    if (token) {
      const getVerificationStatus = async () => {
        try {
          console.log("Checking email verification status...");

          const response = await axios.get(checkAuthEmailVerified, {
            headers: { authorization: `Bearer ${token}` },
          });

          if (response.status === 200) {
            console.log("Verification API Response:", response.data);

            // Update only if 'verified' exists
            if (typeof response.data.verified !== "undefined") {
              setIsVerified(response.data.verified);

              console.log(
                response.data.verified
                  ? "Email is verified"
                  : "Email is NOT verified"
              );
            } else {
              console.warn("Verified field missing in API response");
            }
          }
        } catch (error) {
          console.error(
            "Error while checking verification:",
            error.response?.data || error.message
          );

          // Fallback: assume verified to avoid blocking UI
          setIsVerified(true);
        }
      };

      getVerificationStatus();
    }
  }, [token]);

  /* Attach mount point to first `.main-panel .content-wrapper` so the bar lives in the body column, not under the header */
  useLayoutEffect(() => {
    if (mode !== "globalPortal" || typeof document === "undefined") {
      setBannerMountEl(null);
      return undefined;
    }

    let cancelled = false;

    const ensureMount = () => {
      if (cancelled) return;
      const cw = document.querySelector(".main-panel .content-wrapper");
      if (!cw) {
        setBannerMountEl(null);
        return;
      }
      let mount = cw.querySelector(`#${EMAIL_BANNER_MOUNT_ID}`);
      if (!mount) {
        mount = document.createElement("div");
        mount.id = EMAIL_BANNER_MOUNT_ID;
        cw.prepend(mount);
      }
      setBannerMountEl(mount);
    };

    ensureMount();
    const poll = window.setInterval(ensureMount, 200);
    const stopPoll = window.setTimeout(() => window.clearInterval(poll), 6000);
    window.addEventListener("popstate", ensureMount);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      window.clearTimeout(stopPoll);
      window.removeEventListener("popstate", ensureMount);
      const orphan = document.getElementById(EMAIL_BANNER_MOUNT_ID);
      if (orphan && orphan.parentNode) {
        orphan.remove();
      }
      setBannerMountEl(null);
    };
  }, [mode, token, contentKey]);

  useEffect(() => {
    if (mode !== "globalPortal" || !isVerified) return undefined;
    const t = window.requestAnimationFrame(() => {
      const orphan = document.getElementById(EMAIL_BANNER_MOUNT_ID);
      if (orphan && orphan.parentNode) {
        orphan.remove();
      }
      setBannerMountEl(null);
    });
    return () => window.cancelAnimationFrame(t);
  }, [isVerified, mode]);

  // Reset timer when OTP modal opens
  useEffect(() => {
    if (showOtpModal) {
      // Start 30-second timer when modal opens
      setTimer(30);
      setCanResend(false);
    } else {
      // Reset timer when modal closes
      setTimer(0);
      setCanResend(true);
    }
  }, [showOtpModal]);

  // Send OTP API call
  const handleSendOTP = async () => {
    try {
      console.log("Sending OTP request...");
      setLoading(true);

      const response = await axios.post(
        sendOtpAuthEmailVerification,
        {}, // empty body
        {
          headers: { authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        console.log("OTP sent successfully:", response.data);
        addToast("success", response.data.message || "OTP sent successfully");
        
        // Close first modal and open OTP verification modal
        setShowVerifyModal(false);
        setShowOtpModal(true);
        // Timer will start automatically via useEffect when modal opens
      }
    } catch (error) {
      if (error.response && error.response.data.message) {
        addToast("error", error.response.data.message);
      } else {
        addToast("error", error.message);
      }
      console.log(error);
    }
    finally {
      setLoading(false);
    }
  };

  // Verify OTP API call
  const handleVerifyOTP = async () => {
    if (!otp || otp.trim() === "") {
      addToast("error", "Please enter the verification code");
      return;
    }

    try {
      console.log("Verifying OTP...");
      setOtpLoading(true);

      const response = await axios.post(
        verifyOtpAuthEmailVerification,
        { otp: otp }, // Send OTP in request body
        {
          headers: { authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        console.log("OTP verified successfully:", response.data);
        addToast("success", response.data.message || "Email verified successfully");
        
        // Update verification status
        setIsVerified(true);
        
        // Close OTP modal
        setShowOtpModal(false);
        setOtp("");
        // Reset timer
        setTimer(0);
        setCanResend(true);
      }
    } catch (error) {
      if (error.response && error.response.data.message) {
        addToast("error", error.response.data.message);
      } else {
        addToast("error", error.message || "Invalid OTP. Please try again.");
      }
      console.log(error);
    }
    finally {
      setOtpLoading(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (e) => {
    // Only allow numbers and limit to 6 digits (adjust based on your OTP length)
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setOtp(value);
  };

  // Resend OTP with timer
  const handleResendOTP = async () => {
    if (!canResend) {
      addToast("info", `Please wait ${timer} seconds before requesting again`);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        sendOtpAuthEmailVerification,
        {},
        {
          headers: { authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        addToast("success", response.data.message || "OTP resent successfully");
        
        // Reset timer to 30 seconds
        setTimer(30);
        setCanResend(false);
      }
    } catch (error) {
      if (error.response && error.response.data.message) {
        addToast("error", error.response.data.message);
      } else {
        addToast("error", error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Format timer display (mm:ss)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const inlineBannerStyle = {
    position: "sticky",
    top: `${navOffsetPx}px`,
    zIndex: 1020,
    width: "100%",
    marginBottom: "1rem",
    padding: "12px 20px",
    backgroundColor: "#b71c1c",
    color: "#ffffff",
    fontSize: "14px",
    lineHeight: 1.5,
    textAlign: "center",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.18)",
    borderRadius: "4px",
    border: "1px solid #8b0000",
  };

  /** In-flow banner inside `.content-wrapper` — width is the main column only */
  const bodyColumnBannerWrapStyle = {
    width: "100%",
    marginBottom: "1.25rem",
    boxSizing: "border-box",
  };

  const globalPortalBannerCardStyle = {
    pointerEvents: "auto",
    width: "100%",
    margin: 0,
    padding: "14px 22px",
    backgroundColor: "#b71c1c",
    color: "#ffffff",
    fontSize: "14px",
    lineHeight: 1.5,
    textAlign: "center",
    cursor: "pointer",
    borderRadius: "12px",
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
    border: "none",
  };

  const openVerifyModal = () => setShowVerifyModal(true);

  const emailBannerCard = (
    <div
      role="alert"
      tabIndex={0}
      className="vyfoo-global-email-banner"
      onClick={openVerifyModal}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openVerifyModal();
        }
      }}
      style={globalPortalBannerCardStyle}
    >
      <strong style={{ fontWeight: 600 }}>Action required:</strong>{" "}
      Your email address is not verified.{" "}
      <span style={{ textDecoration: "underline", fontWeight: 600 }}>
        Click here
      </span>{" "}
      to verify your email and secure your account.
    </div>
  );

  const modalsAndBackdrop = (
    <>
      {/* Initial Verification Modal */}
      <div
        className={showVerifyModal ? "modal fade show d-block" : "modal fade"}
        tabIndex={-1}
        role="dialog"
        style={{
          display: showVerifyModal ? "block" : "none",
          zIndex: mode === "globalPortal" ? 1060 : undefined,
        }}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content w-85">

            {/* Modal Header */}
            <div className="modal-header py-2">
              <h5 className="modal-title">Verify your email</h5>
              <button
                type="button"
                className="close"
                onClick={() => {
                  console.log("Modal closed");
                  setShowVerifyModal(false);
                }}
              >
                <span>×</span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body py-2">
              <h5>Send a verification code?</h5>
              <p className="text-muted mt-2">
                We&apos;ll email a one-time verification code to the address on your account.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer" style={{ padding: "15px 0px" }}>

              {/* Send OTP Button */}
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendOTP}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send OTP"}
              </button>

              {/* Cancel Button */}
              <button
                type="button"
                className="btn btn-danger me-3"
                onClick={() => {
                  console.log("User cancelled verification");
                  setShowVerifyModal(false);
                }}
              >
                Cancel
              </button>

            </div>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal */}
      <div
        className={showOtpModal ? "modal fade show d-block" : "modal fade"}
        tabIndex={-1}
        role="dialog"
        style={{
          display: showOtpModal ? "block" : "none",
          zIndex: mode === "globalPortal" ? 1060 : undefined,
        }}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content w-85">

            {/* Modal Header */}
            <div className="modal-header py-2">
              <h5 className="modal-title">Enter verification code</h5>
              <button
                type="button"
                className="close"
                onClick={() => {
                  console.log("OTP modal closed");
                  setShowOtpModal(false);
                  setOtp("");
                  setTimer(0);
                  setCanResend(true);
                }}
              >
                <span>×</span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body py-2">
              <h5>Check your inbox</h5>
              <p className="text-muted">
                Enter the 6-digit code we sent to your email. It may take a minute to arrive.
              </p>
              
              <div className="form-group mt-3">
                <label htmlFor="otpInput">Verification code</label>
                <input
                  type="text"
                  id="otpInput"
                  className="form-control"
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={handleOtpChange}
                  maxLength={6}
                  autoFocus
                />
              </div>
              
              <div className="mt-2 d-flex align-items-center">
                <button
                  type="button"
                  className="btn btn-link p-0 me-2"
                  onClick={handleResendOTP}
                  disabled={loading || !canResend}
                  style={{ fontSize: "14px" }}
                >
                  {loading ? "Sending..." : "Resend code"}
                </button>
                {!canResend && timer > 0 && (
                  <span className="text-muted" style={{ fontSize: "12px" }}>
                    ({formatTime(timer)})
                  </span>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer" style={{ padding: "15px 0px" }}>

              {/* Verify OTP Button */}
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleVerifyOTP}
                disabled={otpLoading || !otp}
              >
                {otpLoading ? "Verifying..." : "Verify email"}
              </button>

              {/* Cancel Button */}
              <button
                type="button"
                className="btn btn-danger me-3"
                onClick={() => {
                  console.log("User cancelled OTP verification");
                  setShowOtpModal(false);
                  setOtp("");
                  setTimer(0);
                  setCanResend(true);
                }}
              >
                Cancel
              </button>

            </div>
          </div>
        </div>
      </div>

      {/* Add backdrop when modals are open */}
      {(showVerifyModal || showOtpModal) && (
        <div
          className="modal-backdrop fade show"
          style={mode === "globalPortal" ? { zIndex: 1055 } : undefined}
        />
      )}
    </>
  );

  if (mode === "globalPortal" && typeof document !== "undefined") {
    return (
      <>
        {bannerMountEl &&
          !isVerified &&
          createPortal(
            <div style={bodyColumnBannerWrapStyle}>{emailBannerCard}</div>,
            bannerMountEl
          )}
        {createPortal(modalsAndBackdrop, document.body)}
      </>
    );
  }

  return (
    <>
      {!isVerified && (
        <div
          role="alert"
          tabIndex={0}
          onClick={openVerifyModal}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openVerifyModal();
            }
          }}
          style={inlineBannerStyle}
        >
          <strong style={{ fontWeight: 600 }}>Action required:</strong>{" "}
          Your email address is not verified.{" "}
          <span style={{ textDecoration: "underline", fontWeight: 600 }}>
            Click here
          </span>{" "}
          to verify your email and secure your account.
        </div>
      )}
      {modalsAndBackdrop}
    </>
  );
}